/**
 * 日期/时间相关
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-06-22 10:34:00
 *      ┌─┐       ┌─┐ + +
 *   ┌──┘ ┴───────┘ ┴──┐++
 *   │                 │
 *   │       ───       │++ + + +
 *   ███████───███████ │+
 *   │                 │+
 *   │       ─┴─       │
 *   │                 │
 *   └───┐         ┌───┘
 *       │         │
 *       │         │   + +
 *       │         │
 *       │         └──────────────┐
 *       │                        │
 *       │                        ├─┐ o O ) )
 *       │                        ┌─┘
 *       │                        │
 *       └─┐  ┐  ┌───────┬──┐  ┌──┘  + + + +
 *         │ ─┤ ─┤       │ ─┤ ─┤
 *         └──┴──┘       └──┴──┘  + + + +
 *                神兽保佑
 *               代码无BUG!
 */
/**
 * 秒和毫秒的进率
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-26 09:34:34
 */
export const MILLISECOND_TO_SECOND = 1000;
/**
 * 时和秒的进率
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-26 09:35:31
 */
export const SECOND_TO_HOUR = 3600;
/**
 * 天和时的进率
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-26 09:36:42
 */
export const HOUR_TO_DAY = 24;
/**
 * 一天的毫秒数
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-06-22 10:38:16
 */
export const DAY_MILLISECONDS = SECOND_TO_HOUR * MILLISECOND_TO_SECOND * HOUR_TO_DAY;
/**
 * 获取时间戳
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-06-22 10:38:26
 * @param {Date} date
 */
export function getTimestamp(date = new Date()) {
    return date.getTime();
}
/**
 * 获取当前时间戳
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-06-22 10:38:53
 */
export function getNowTimestamp() {
    return getTimestamp();
}
/**
 * 获取当天零点时间戳
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-06-22 10:39:04
 */
export function getTodayTimestamp() {
    const now = getNowTimestamp();
    return now - (now % DAY_MILLISECONDS);
}
/**
 * ```
 * 初始化日期表单项
 * 传入字段值为 0 时视作空数据处理
 * 数据库中存储为秒，实际转换为毫秒
 * ```
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-26 09:30:21
 * @param {number} field 日期值，来自接口字段
 * @return {FormDateField} 表单中使用的日期数据
 */
export const initialDateField = (field) => field === 0
    ? ''
    : new Date(field * MILLISECOND_TO_SECOND - new Date().getTimezoneOffset() * 60_000);
/**
 * ```
 * 将表单项日期数据转为接口能用的日期值(秒)
 * 按照可逆的原则，函数设计成传入空字符串会将数据还原成 0
 * 实际业务场景中一般会进行校验并拦截掉
 * ```
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-26 09:43:25
 */
export const formDate2Params = (date) => date === '' ? 0 : Math.trunc(date.getTime() / MILLISECOND_TO_SECOND);
/**
 * 根据给定的时间获取当天最后一秒时间点
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-31 19:17:54
 * @param {number | FormDateField} timestamp 时间
 * @return {Date}
 */
export const getDayEndTimestamp = (timestamp) => {
    const newTimestamp = timestamp === '' ? 0 : typeof timestamp === 'number' ? timestamp : timestamp.getTime();
    return new Date(newTimestamp - (newTimestamp % DAY_MILLISECONDS) + DAY_MILLISECONDS - MILLISECOND_TO_SECOND);
};
/**
 * 格式化日期为 YYYY-MM-DD 字符串
 * @author Jerry <superzcj_001@163.com>
 * @since  2020-08-26 21:04:07
 */
export const toDateString = (timestamp) => {
    if (timestamp === '') {
        return '';
    }
    const date = typeof timestamp === 'number' ? new Date(timestamp) : timestamp;
    return date?.toJSON()?.replace(/T.+/, '') ?? '';
};
/**
 * 获取日期时间字符串
 * 空字符串和0返回 --
 * 其余数值或日期对象返回相应日期时间
 * @author  Jerry <superzcj_001@163.com>
 * @since   2020-09-17 21:57:25
 */
export const toDateTimeString = (timestamp) => {
    if (timestamp === '' || timestamp === 0) {
        return '--';
    }
    const date = typeof timestamp === 'number'
        ? new Date(timestamp * MILLISECOND_TO_SECOND - new Date().getTimezoneOffset() * 60_000)
        : timestamp;
    return date
        ?.toJSON()
        .replace(/T|(?:\.\d+Z)$/gu, ' ')
        .trim();
};
export const formDateRange2Params = (dateRange) => {
    const [startTimestamp, endTimestamp] = dateRange;
    if (startTimestamp === '' || endTimestamp === '') {
        return ['', ''];
    }
    return [
        formDate2Params(new Date(startTimestamp)),
        formDate2Params(getDayEndTimestamp(new Date(endTimestamp))),
    ];
};
//# sourceMappingURL=time.js.map